import * as yup from 'yup';
import { DocumentNode } from 'graphql';
import { Trans, useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-form-state-manager';
import sortBy from 'lodash/sortBy';

import { GetTeamQuery as GetTeam } from '__generated__/graphql';
import { getServerErrors } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import ContactPicker, { Participant } from '../Contacts/ContactPicker';
import EditRegistrationBackendMutation from '../Participant/About/EditRegistrationBackendMutation';
import GetTeamRegistrationCandidatesQuery from './GetTeamRegistrationCandidatesQuery';
import UI from '../../../common/UI';
import useMutation from '../../../api/useMutation';
import useQuery from '../../../api/useQuery';
import useValidation from '../../../common/useValidation';

type Team = GetTeam['team'];

export interface AddTeamMemberFormProps {
  team: Team;
  refetchQueries?: DocumentNode[];
  onClose?: () => void;
}

const schema = yup.object().shape({
  registration_id: yup.string(),
});

const AddTeamMemberForm = ({ team, refetchQueries = [], onClose }: AddTeamMemberFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();

  const form = useForm({
    values: {
      registration_id: null,
    },
  });

  const [participant, setParticipant] = useState<Participant>(null);

  const { data } = useQuery(GetTeamRegistrationCandidatesQuery, {
    variables: {
      participant_id: participant?.id,
    },
    skip: !participant,
  });

  const [addMember, { error, loading }] = useMutation(
    EditRegistrationBackendMutation,
    {
      variables: {
        input: {
          id: form.values.registration_id,
          team: {
            team_type: { id: team.team_type.id },
            id: team.id,
          },
        },
      },
      refetchQueries,
      onCompleted: () => {
        notifier.success(t('notifications.team_member_added'));
        onClose?.();
      },
    },
  );

  const { valid, errors } = useValidation({
    schema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const teamMemberIds = useMemo(() => team.registrations.map((ticket) => ticket.id), [team]);

  const candidates = useMemo(() => (
    sortBy(data?.participant.registrations.filter((registration) => (
      !teamMemberIds.includes(registration.id) && registration.ticket?.event.id === team.event.id
    )) || [], 'ticket.position')
  ), [data, teamMemberIds, team]);

  useEffect(() => {
    form.set('registration_id', candidates[0]?.id || null);
  }, [candidates]);

  const selectedRegistration = data?.participant.registrations.filter(
    (registration) => registration.id === form.values.registration_id,
  )[0] || null;

  return (
    <UI.Form onSubmit={() => addMember()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>{t('add_a_team_member')}</UI.H4>

          <UI.Paragraph sc={{ mb: 0 }}>
            {t('add_team_member_description')}
          </UI.Paragraph>
        </UI.Div>

        <UI.ServerErrors error={error} />

        <UI.HR />

        <UI.InputGroup sc={{ valid: !errors.participant_id, touched: form.touched.participant_id }}>
          <UI.InputLabel>
            {t('contact')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <ContactPicker
            participant={participant}
            nullable={false}
            filters={{ event: team.event.id }}
            onChange={(participant) => setParticipant(participant)}
          />
          <UI.ErrorMessages attribute={t('event_title')} errors={errors.participant_id} />
        </UI.InputGroup>

        {candidates.length === 0 && data && (
          <UI.Warning>
            {t('not_possible_to_add_team_member')}
          </UI.Warning>
        )}

        {candidates.length > 0 && (
          <UI.InputGroup sc={{ valid: !errors.registration_id, touched: form.touched.registration_id }}>
            <UI.InputLabel htmlFor="Purchase_0">
              {t('ticket')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>

            {candidates.map((registration) => (
              <UI.Radio
                onChange={() => form.set('registration_id', registration.id)}
                checked={form.values.registration_id === registration.id}
                key={registration.id}
              >
                <UI.GridContainer sc={{ columns: ['1fr', '3fr 1fr'] }}>
                  <UI.Div sc={{ muted: !registration.purchase.active }}>
                    <UI.Strong>
                      <UI.Icon>
                        <UI.Icons.Ticket />
                      </UI.Icon>
                      {' '}
                      {registration.ticket.title}
                    </UI.Strong>
                  </UI.Div>
                  <UI.Div sc={{ muted: true, small: true, textAlign: 'right' }}>
                    {`ATL${registration.sequence}`}
                  </UI.Div>
                </UI.GridContainer>
              </UI.Radio>
            ))}
          </UI.InputGroup>
        )}

      </UI.FormGrid>

      {valid && selectedRegistration?.team && (
        <UI.Info sc={{ mt: 2 }}>
          <Trans
            i18nKey="switch_teams_info"
            values={{
              fromTeam: selectedRegistration?.team.title,
              toTeam: team.title,
            }}
          >
            <UI.Strong />
          </Trans>
        </UI.Info>
      )}

      <UI.HR />

      <UI.FormButtons
        form={form}
        submitLabel={t('add_team_member')}
        loading={loading}
        valid={valid}
      />
    </UI.Form>
  );
};

export default AddTeamMemberForm;
